const toNumberOrNull = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

export const ensureLatLng = (node = {}) => {
  const lat = toNumberOrNull(
    node?.coords?.[0] ??
      node?.coordinates?.[0] ??
      node?.location?.coords?.[0] ??
      node?.location?.coordinates?.[0] ??
      node?.latLng?.lat ??
      node?.latLng?.latitude ??
      node?.location?.latLng?.lat ??
      node?.location?.latLng?.latitude ??
      node?.location?.lat ??
      node?.location?.latitude ??
      node?.lat ??
      node?.latitude,
  );
  const lng = toNumberOrNull(
    node?.coords?.[1] ??
      node?.coordinates?.[1] ??
      node?.location?.coords?.[1] ??
      node?.location?.coordinates?.[1] ??
      node?.latLng?.lng ??
      node?.latLng?.longitude ??
      node?.location?.latLng?.lng ??
      node?.location?.latLng?.longitude ??
      node?.location?.lng ??
      node?.location?.longitude ??
      node?.lng ??
      node?.longitude,
  );
  return { lat, lng };
};

// Treat near-identical coordinates as the same node (loop detection).
const sameLatLng = (a, b, tol = 1e-5) =>
  Number.isFinite(a?.lat) &&
  Number.isFinite(a?.lng) &&
  Number.isFinite(b?.lat) &&
  Number.isFinite(b?.lng) &&
  Math.abs(a.lat - b.lat) <= tol &&
  Math.abs(a.lng - b.lng) <= tol;

const normalizeGeometry = (node = {}) => {
  const geo = node.geometry || {};
  const rawType = geo.kind || geo.type || node.geometryType || node.shapeMode || node.kind;
  const type = rawType ? String(rawType).toLowerCase() : null;
  const zoneId = geo.zoneId ?? node.zoneId ?? node.zone_id ?? null;
  const isZone = type === 'zone' || geo.isZone || zoneId != null;
  if (type === 'polygon' || type === 'zone') {
    const pts = Array.isArray(geo.points) ? geo.points : [];
    const resolvedType = isZone ? 'zone' : type;
    return { type: resolvedType, points: pts, zoneId, isZone };
  }
  if (type === 'crossline') {
    return {
      type: 'crossline',
      points: Array.isArray(geo.points) ? geo.points : [],
      radiusMeters: geo.radiusMeters ?? geo.radius ?? node.radiusMeters ?? node.radius ?? 75,
      angleDeg: geo.angleDeg ?? geo.angleRad ?? geo.handleBearing ?? null,
    };
  }
  // default circle
  const radius = geo.radiusMeters ?? geo.radius ?? node.radiusMeters ?? node.radius ?? 75;
  return { type: 'circle', radiusMeters: radius };
};

const fallbackLabel = (node, idx) => {
  if (typeof node?.label === 'string' && node.label.trim().length) return node.label;
  if (typeof node?.name === 'string' && node.name.trim().length) return node.name;
  return `Checkpoint ${idx + 1}`;
};

const toCheckpoint = (node, idx) => {
  const { lat, lng } = ensureLatLng(node);
  const radius = toNumberOrNull(node?.radius);
  const etaSeconds = toNumberOrNull(node?.etaSeconds);
  const handleBearing = toNumberOrNull(node?.handleBearing);
  const stopIndex = Number.isInteger(node?.stopIndex) ? node.stopIndex : idx + 1;
  const timeWindowMinutes = toNumberOrNull(node?.bonusTime);
  const stopTimeMinutes = toNumberOrNull(node?.waitTime);
  const modeRaw = (node?.travelMode || node?.mode || node?.leg?.travelMode || '')
    .toString()
    .toUpperCase();
  const mode =
    modeRaw === 'WALK' || modeRaw === 'WALKING'
      ? 'WALKING'
      : modeRaw === 'DRIVE' || modeRaw === 'DRIVING'
        ? 'DRIVING'
        : null;

  const zoneId = node?.zoneId ?? node?.zone_id ?? node?.geometry?.zoneId ?? null;
  return {
    id: node?.id ?? `node-${idx}`,
    label: fallbackLabel(node, idx),
    address: node?.address || node?.label || node?.name || '',
    lat: lat ?? '',
    lng: lng ?? '',
    radiusMeters: radius ?? 75,
    stopTimeMinutes: stopTimeMinutes ?? 0,
    timeWindowMinutes: timeWindowMinutes ?? 0,
    timeWindowSeconds: toNumberOrNull(node?.timeWindowSeconds),
    etaSeconds: etaSeconds ?? null,
    stopIndex,
    handleBearing: handleBearing ?? 0,
    placeId: node?.placeId || '',
    isCheckpoint: true,
    kind: node?.type || 'checkpoint',
    coordinates:
      Array.isArray(node?.coords) && node.coords.length >= 2
        ? node.coords
        : lat != null && lng != null
          ? [lat, lng]
          : null,
    geometry: normalizeGeometry(node),
    zoneId,
    mode: mode || 'DRIVING',
  };
};

export const splitTourNodes = (nodes = []) => {
  const list = Array.isArray(nodes) ? nodes.filter(Boolean) : [];
  if (!list.length) return { originNode: null, checkpoints: [] };

  const originIndex = list.findIndex((node) => (node?.type || '').toLowerCase() === 'origin');
  const resolvedOriginIndex = originIndex >= 0 ? originIndex : 0;
  const originNode = list[resolvedOriginIndex];
  const originLatLng = ensureLatLng(originNode);
  const lastIndex = list.length - 1;
  const lastNode = list[lastIndex];
  const isLooped =
    lastIndex > resolvedOriginIndex && sameLatLng(originLatLng, ensureLatLng(lastNode));
  const checkpointSource = list.filter(
    (_, idx) => idx !== resolvedOriginIndex && (!isLooped || idx !== lastIndex),
  );

  const checkpoints = checkpointSource.map((node, idx) => toCheckpoint(node, idx));

  return {
    originNode: originNode || null,
    checkpoints,
    isLooped,
  };
};

const resolveOriginNode = (tour) =>
  tour?.originNode ||
  tour?.origin ||
  tour?.start ||
  tour?.startLocation ||
  tour?.start_node ||
  tour?.startNode ||
  null;

export const deriveCheckpointsFromTour = (tour) => {
  if (!tour) return { originNode: null, checkpoints: [] };
  if (Array.isArray(tour?.nodes) && tour.nodes.length) {
    return splitTourNodes(tour.nodes);
  }

  if (Array.isArray(tour?.stops) && tour.stops.some((stop) => stop?.type)) {
    return splitTourNodes(tour.stops);
  }
  if (Array.isArray(tour?.checkpoints) && tour.checkpoints.some((stop) => stop?.type)) {
    return splitTourNodes(tour.checkpoints);
  }

  const checkpoints = Array.isArray(tour?.checkpoints)
    ? tour.checkpoints
    : Array.isArray(tour?.stops)
      ? tour.stops
      : [];

  return {
    originNode: resolveOriginNode(tour),
    checkpoints,
    isLooped: false,
  };
};
